import { matchPath } from 'react-router'
import { NavLinkProps, useLocation } from 'react-router-dom'
import { Box, Inline, Columns, Column, ResponsiveRender } from '@machobear/design-system'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints } from '@/styles'
import { handleTabByKeyboard } from '@/utils/handleTabByKeyboard'
import { CssUnit } from '@/types'

type Theme = 'primary' | 'secondary'
type Size = 'medium' | 'small'

type TabProps = Omit<NavLinkProps, 'to'> & {
  key: string
  to?: string
  onClick?: () => void
  active?: boolean
}

type TabMenuStyle = {
  theme: Theme
  size: Size
  height?: CssUnit
}

type TabMenuAltProps = TabMenuStyle & {
  options: TabProps[]
  children?: React.ReactNode
  setCurrentTab: (key: string) => void
}

const MyListingsTabs: React.FC<TabMenuAltProps> = ({ options, children, setCurrentTab }) => {
  const displayBelowDesktop = useMediaQuery(`(max-width: ${breakpoints.desktop})`)
  const location = useLocation()
  const url = `${location.pathname}${location.search}`
  const matches = options
    .map(({ key, to, isActive, ...rest }) =>
      to ? (matchPath(url, { path: to, ...rest }) ? key : '') : isActive ? key : ''
    )
    .filter(v => !!v)
  const current = matches?.[0] ?? ''
  const isIntegrations = current === 'integrations'
  return (
    <Box
      display="flex"
      justifyContent="center"
      background={isIntegrations ? 'formAccentSoft' : 'neutralLight'}
      width="full"
      paddingTop={{ mobile: 'xsmall', tablet: 'xsmall', desktop: 'none' }}
    >
      <ResponsiveRender above="mobile">
        <Box
          style={{ maxWidth: '1130px', flex: 1, margin: displayBelowDesktop ? '0 30px' : '0 50px' }}
          display="flex"
          justifyContent="flexStart"
          role="tablist"
          aria-label="Listing Platform Type Tabs"
          tabIndex={0}
          onKeyDown={e => handleTabByKeyboard(e, options, current, setCurrentTab)}
        >
          <Inline space="xsmall" alignY="center" align="left">
            {options.map(({ key, children, onClick, isActive }) => (
              <Box
                tabIndex={-1}
                component="button"
                role="tab"
                id={`tab-${key}`}
                aria-selected={isActive ? true : false}
                aria-controls={`tab-panel-${key}`}
                cursor="pointer"
                key={key}
                paddingX="medium"
                paddingY="small"
                background={isActive ? 'white' : 'disabled'}
                borderTopRightRadius="large"
                borderTopLeftRadius="large"
                border="borderNeutralLight"
                style={{ borderBottom: 'none' }}
                onClick={onClick}
              >
                {children}
              </Box>
            ))}
          </Inline>
        </Box>
      </ResponsiveRender>

      <ResponsiveRender below="tablet">
        <Box
          marginX="medium"
          marginY="none"
          width="full"
          role="tablist"
          aria-label="Listing Platform Type Tabs"
        >
          <Columns space="xsmall">
            {options.map(({ key, children, onClick, isActive }) => (
              <Column width="1/2" key={key}>
                <Box
                  width="full"
                  paddingX="medium"
                  paddingY="small"
                  background={isActive ? 'white' : 'disabled'}
                  borderTopRightRadius="large"
                  borderTopLeftRadius="large"
                  border="borderNeutralLight"
                  style={{ borderBottom: 'none' }}
                  tabIndex={isActive ? 0 : -1}
                  component="button"
                  role="tab"
                  id={`tab-${key}`}
                  aria-selected={isActive ? true : false}
                  aria-controls={`tab-panel-${key}`}
                  cursor="pointer"
                  onClick={onClick}
                >
                  {children}
                </Box>
              </Column>
            ))}
          </Columns>
        </Box>
      </ResponsiveRender>

      {children}
    </Box>
  )
}

export default MyListingsTabs
